import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  MessageSquare, Mail, Phone, RefreshCw, Search, CheckCircle2, XCircle, Clock,
  Send, Inbox, Eye, Sparkles, Radio, Building2, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const channelMeta = {
  sms: { icon: Phone, color: 'text-rose-600', bg: 'bg-rose-50 border-rose-300', label: 'SMS' },
  whatsapp: { icon: MessageSquare, color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-300', label: 'WhatsApp' },
  email: { icon: Mail, color: 'text-blue-600', bg: 'bg-blue-50 border-blue-300', label: 'Email' },
};

// Phase 66 — visual badge for the route a message took
const routeBadge = (route) => {
  const r = (route || '').toLowerCase();
  if (r === 'academy') {
    return <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300 text-[10px]" title="Sent via Academy's own Twilio/Resend keys"><Building2 className="h-3 w-3 mr-1" />Academy</Badge>;
  }
  if (r === 'relay') {
    return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300 text-[10px]" title="Sent via Out-Play Super Admin Relay"><Radio className="h-3 w-3 mr-1" />Out-Play Relay</Badge>;
  }
  if (r === 'academy_then_relay') {
    return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 text-[10px]" title="Academy keys failed → automatically retried via Out-Play Relay"><RefreshCw className="h-3 w-3 mr-1" />Failover</Badge>;
  }
  if (r === 'mocked') {
    return <Badge variant="outline" className="bg-muted text-muted-foreground text-[10px]" title="No keys configured — message was logged but not delivered"><Clock className="h-3 w-3 mr-1" />Mocked</Badge>;
  }
  // Defensive — pre-Phase-66 row missing the route field altogether
  return <Badge variant="outline" className="text-[10px] text-muted-foreground" title="Route unknown (pre-Phase-66 record)">Unknown</Badge>;
};

const statusBadge = (status) => {
  const s = (status || '').toLowerCase();
  if (['sent', 'delivered', 'queued', 'sending', 'accepted'].includes(s)) {
    return <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300 text-[10px]"><CheckCircle2 className="h-3 w-3 mr-1" />{s}</Badge>;
  }
  if (s === 'failed' || s === 'undelivered') {
    return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300 text-[10px]"><XCircle className="h-3 w-3 mr-1" />{s}</Badge>;
  }
  return <Badge variant="outline" className="text-[10px]"><Clock className="h-3 w-3 mr-1" />{s || 'pending'}</Badge>;
};

export default function MessageLogs() {
  const [data, setData] = useState({ logs: [], stats: { total: 0, sms: 0, whatsapp: 0, email: 0, sent: 0, failed: 0 }, next_cursor: null, has_more: false });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filters, setFilters] = useState({ channel: 'all', status: 'all', route: 'all', days: 7, search: '' });
  const [detail, setDetail] = useState(null);

  const [showSendTest, setShowSendTest] = useState(false);
  const [testForm, setTestForm] = useState({ to: '', message: 'Hello from Out-Play! Test broadcast.', channel: 'whatsapp' });
  const [sendingTest, setSendingTest] = useState(false);

  const fetchLogs = useCallback(async (append = false) => {
    if (append) setLoadingMore(true); else setRefreshing(true);
    try {
      const params = { days: filters.days, limit: 50 };
      if (filters.channel !== 'all') params.channel = filters.channel;
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.route !== 'all') params.route = filters.route;
      if (append && data.next_cursor) params.cursor = data.next_cursor;
      const res = await api.get('/admin/message-logs', { params });
      if (append) {
        setData(prev => ({
          ...res.data,
          logs: [...(prev.logs || []), ...(res.data.logs || [])],
        }));
      } else {
        setData(res.data);
      }
    } catch (err) {
      toast.error('Failed to load logs');
    } finally {
      setLoading(false);
      setRefreshing(false);
      setLoadingMore(false);
    }
  }, [filters.channel, filters.status, filters.days, data.next_cursor]);

  // Reset & re-fetch when filters change
  useEffect(() => {
    fetchLogs(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.channel, filters.status, filters.route, filters.days]);

  const filteredLogs = (data.logs || []).filter(l => {
    if (!filters.search) return true;
    const q = filters.search.toLowerCase();
    return (l.to || '').toLowerCase().includes(q) || (l.subject || '').toLowerCase().includes(q);
  });

  const handleSendTest = async () => {
    if (!testForm.to || !testForm.message) { toast.error('Recipient and message required'); return; }
    setSendingTest(true);
    try {
      // Simple announcement-based test: send via /announcements/send with a single-recipient hack
      // Better: re-use Verify endpoints which take exact recipient
      if (testForm.channel === 'email') {
        const r = await api.post('/settings/integrations/resend/test', { to_email: testForm.to });
        if (r.data.success) toast.success(r.data.message); else toast.error(r.data.message);
      } else {
        const r = await api.post('/settings/integrations/twilio/test', { channel: testForm.channel, to_number: testForm.to });
        if (r.data.success) toast.success(r.data.message); else toast.error(r.data.message);
      }
      setShowSendTest(false);
      setTimeout(() => fetchLogs(false), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) return (
    <div className="space-y-4" data-testid="message-logs-loading">
      <Skeleton className="h-8 w-64" />
      <div className="grid grid-cols-3 gap-3">{[1,2,3].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
      <Skeleton className="h-96 rounded-xl" />
    </div>
  );

  const total = data.stats?.total || 0;
  const sentRate = total ? Math.round((data.stats.sent / total) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="message-logs-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-semibold flex items-center gap-2"><Inbox className="h-6 w-6 text-primary" /> Delivery Logs</h1>
          <p className="text-sm text-muted-foreground">Last {filters.days} days · {total} message{total !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => fetchLogs(false)} disabled={refreshing} data-testid="logs-refresh">
            <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button size="sm" onClick={() => setShowSendTest(true)} data-testid="logs-send-test">
            <Send className="h-4 w-4 mr-1" /> Send Test
          </Button>
        </div>
      </div>

      {/* Phase 70 — actionable banner when ALL recent Twilio rows are mocked (= no relay creds set) */}
      {(() => {
        const twilioLogs = data.logs.filter(l => l.channel === 'sms' || l.channel === 'whatsapp');
        if (twilioLogs.length === 0) return null;
        const allMocked = twilioLogs.every(l => l.status === 'mocked');
        const reason = twilioLogs.find(l => l.mock_reason)?.mock_reason;
        if (!allMocked || !reason) return null;
        return (
          <div className="rounded-lg border-2 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/20 p-4 flex items-start gap-3" data-testid="mocked-warning-banner">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-200">Twilio Relay Not Configured — All messages are being mocked (not actually sent)</h3>
              <p className="text-xs text-amber-800 dark:text-amber-300 mt-1 leading-relaxed">{reason}</p>
              <a href="/settings" className="text-xs font-medium text-amber-700 dark:text-amber-300 underline mt-2 inline-block">Configure Twilio Now →</a>
            </div>
          </div>
        );
      })()}

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-950/20 dark:to-pink-950/20 border-rose-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <Phone className="h-4 w-4 text-rose-600" />
              <Badge variant="outline" className="text-[9px]">SMS</Badge>
            </div>
            <p className="text-2xl font-display font-bold tabular-nums text-rose-700">{data.stats.sms}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/20 dark:to-teal-950/20 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <MessageSquare className="h-4 w-4 text-emerald-600" />
              <Badge variant="outline" className="text-[9px]">WhatsApp</Badge>
            </div>
            <p className="text-2xl font-display font-bold tabular-nums text-emerald-700">{data.stats.whatsapp}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <Mail className="h-4 w-4 text-blue-600" />
              <Badge variant="outline" className="text-[9px]">Email</Badge>
            </div>
            <p className="text-2xl font-display font-bold tabular-nums text-blue-700">{data.stats.email}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <Badge variant="outline" className="text-[9px]">{sentRate}%</Badge>
            </div>
            <p className="text-2xl font-display font-bold tabular-nums text-emerald-700">{data.stats.sent}</p>
            <p className="text-xs text-emerald-700">Sent</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-rose-50 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <XCircle className="h-4 w-4 text-red-600" />
              <Badge variant="outline" className="text-[9px]">Errors</Badge>
            </div>
            <p className="text-2xl font-display font-bold tabular-nums text-red-700">{data.stats.failed}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <Tabs value={filters.channel} onValueChange={(v) => setFilters({...filters, channel: v})}>
          <TabsList>
            <TabsTrigger value="all" data-testid="logs-tab-all">All</TabsTrigger>
            <TabsTrigger value="sms" data-testid="logs-tab-sms">SMS</TabsTrigger>
            <TabsTrigger value="whatsapp" data-testid="logs-tab-whatsapp">WhatsApp</TabsTrigger>
            <TabsTrigger value="email" data-testid="logs-tab-email">Email</TabsTrigger>
          </TabsList>
        </Tabs>
        <Select value={filters.status} onValueChange={(v) => setFilters({...filters, status: v})}>
          <SelectTrigger className="w-32 h-9 text-xs" data-testid="logs-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any Status</SelectItem>
            <SelectItem value="sent">Sent / Delivered</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.route} onValueChange={(v) => setFilters({...filters, route: v})}>
          <SelectTrigger className="w-36 h-9 text-xs" data-testid="logs-route-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any Route</SelectItem>
            <SelectItem value="academy">Academy Keys</SelectItem>
            <SelectItem value="relay">Out-Play Relay</SelectItem>
            <SelectItem value="academy_then_relay">Failover</SelectItem>
            <SelectItem value="mocked">Mocked</SelectItem>
          </SelectContent>
        </Select>
        <Select value={String(filters.days)} onValueChange={(v) => setFilters({...filters, days: parseInt(v)})}>
          <SelectTrigger className="w-28 h-9 text-xs" data-testid="logs-days-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[1, 7, 14, 30, 90].map(d => <SelectItem key={d} value={String(d)}>{d}d</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-48 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={filters.search}
            onChange={(e) => setFilters({...filters, search: e.target.value})}
            placeholder="Search recipient or subject..."
            className="pl-9 h-9 text-xs"
            data-testid="logs-search"
          />
        </div>
      </div>

      {/* Logs table */}
      <Card>
        <CardContent className="p-0">
          {filteredLogs.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <Sparkles className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p>No messages found in the selected window.</p>
              <p className="text-xs mt-1">Configure Twilio + Resend in <strong>Settings → Integrations & Comms</strong>, then send a fee reminder or announcement to populate this dashboard.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-32">Channel</TableHead>
                  <TableHead>Recipient</TableHead>
                  <TableHead className="hidden md:table-cell">Subject / Body</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden md:table-cell w-40">Route</TableHead>
                  <TableHead className="hidden lg:table-cell">Sent</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLogs.map((l) => {
                  const meta = channelMeta[l.channel] || channelMeta.sms;
                  const Icon = meta.icon;
                  return (
                    <TableRow key={l.id} data-testid={`log-row-${l.id}`} className="cursor-pointer hover:bg-muted/40" onClick={() => setDetail(l)}>
                      <TableCell>
                        <Badge variant="outline" className={`${meta.bg} text-[10px]`}><Icon className={`h-3 w-3 mr-1 ${meta.color}`} /> {meta.label}</Badge>
                      </TableCell>
                      <TableCell className="font-medium text-sm font-mono">{l.to}</TableCell>
                      <TableCell className="hidden md:table-cell text-xs text-muted-foreground line-clamp-1 max-w-md">{l.subject}</TableCell>
                      <TableCell>{statusBadge(l.status)}</TableCell>
                      <TableCell className="hidden md:table-cell" data-testid={`log-row-route-${l.id}`}>{routeBadge(l.route)}</TableCell>
                      <TableCell className="hidden lg:table-cell text-xs text-muted-foreground tabular-nums">{l.created_at ? new Date(l.created_at).toLocaleString() : '—'}</TableCell>
                      <TableCell><Eye className="h-3.5 w-3.5 text-muted-foreground" /></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
        {data.has_more && (
          <div className="flex justify-center py-4 border-t">
            <Button size="sm" variant="outline" onClick={() => fetchLogs(true)} disabled={loadingMore} data-testid="logs-load-more">
              {loadingMore ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
              Load more
            </Button>
          </div>
        )}
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="log-detail-dialog">
          <DialogHeader><DialogTitle>Delivery details</DialogTitle><DialogDescription>Recipient, status, and provider IDs for this message.</DialogDescription></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className={(channelMeta[detail.channel] || channelMeta.sms).bg}>{(channelMeta[detail.channel] || channelMeta.sms).label}</Badge>
                {statusBadge(detail.status)}
                {routeBadge(detail.route)}
              </div>
              <div className="grid grid-cols-[120px_1fr] gap-y-2 text-xs">
                <span className="text-muted-foreground">Recipient</span><span className="font-mono">{detail.to}</span>
                <span className="text-muted-foreground">From</span><span className="font-mono break-all">{detail.from || '—'}</span>
                {detail.reply_to && (<><span className="text-muted-foreground">Reply-To</span><span className="font-mono break-all">{detail.reply_to}</span></>)}
                {detail.academy_name && (<><span className="text-muted-foreground">Academy</span><span>{detail.academy_name}</span></>)}
                <span className="text-muted-foreground">Sent at</span><span>{detail.created_at ? new Date(detail.created_at).toLocaleString() : '—'}</span>
                {detail.twilio_sid && (<><span className="text-muted-foreground">Twilio SID</span><span className="font-mono break-all">{detail.twilio_sid}</span></>)}
                {detail.twilio_error_code && (<><span className="text-muted-foreground">Twilio Error</span><span className="font-mono text-red-600">[{detail.twilio_error_code}] {detail.twilio_error_message || ''}</span></>)}
                {detail.twilio_error_explainer && (<><span className="text-muted-foreground">Why it failed</span><span className="text-amber-700 leading-snug">{detail.twilio_error_explainer}</span></>)}
                {detail.twilio_status_updated_at && (<><span className="text-muted-foreground">Status updated</span><span>{new Date(detail.twilio_status_updated_at).toLocaleString()}</span></>)}
                {detail.resend_id && (<><span className="text-muted-foreground">Resend ID</span><span className="font-mono break-all">{detail.resend_id}</span></>)}
                {detail.retry_after_academy_fail && (<><span className="text-muted-foreground">Failover</span><span className="text-amber-700">Academy keys failed → retried via Out-Play Relay</span></>)}
                {detail.previous_error && (<><span className="text-muted-foreground">Academy error</span><span className="text-xs font-mono break-all text-red-600">{detail.previous_error}</span></>)}
              </div>
              {detail.subject && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Body / Subject</p>
                  <div className="p-3 rounded-lg bg-muted/50 text-sm whitespace-pre-wrap">{detail.subject}</div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Test dialog */}
      <Dialog open={showSendTest} onOpenChange={setShowSendTest}>
        <DialogContent className="max-w-sm" data-testid="send-test-dialog">
          <DialogHeader><DialogTitle>Send Test Message</DialogTitle><DialogDescription>Send a one-off test through your configured Twilio or Resend integration.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium">Channel</label>
              <Select value={testForm.channel} onValueChange={(v) => setTestForm({...testForm, channel: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sms">SMS</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium">{testForm.channel === 'email' ? 'Recipient Email' : 'Recipient Phone (E.164)'}</label>
              <Input
                value={testForm.to}
                onChange={(e) => setTestForm({...testForm, to: e.target.value})}
                placeholder={testForm.channel === 'email' ? 'user@example.com' : '+919876543210'}
                data-testid="send-test-recipient"
              />
            </div>
            <Button onClick={handleSendTest} disabled={sendingTest || !testForm.to} className="w-full" data-testid="send-test-submit">
              <Send className="h-4 w-4 mr-2" /> {sendingTest ? 'Sending...' : 'Send Test'}
            </Button>
            <p className="text-xs text-muted-foreground">Uses your stored {testForm.channel === 'email' ? 'Resend' : 'Twilio'} credentials. Ensure they're configured in Settings → Integrations & Comms.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
